import React, { useState } from 'react';
import { MapPin, Phone, Clock, Mail, User, MessageSquare } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { submitContactForm, contactInfo, storeHours } from '../mock';
import { toast } from 'sonner';

const Location = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await submitContactForm(formData);
      if (response.success) {
        toast.success(response.message);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try calling us instead.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section 
        className="py-20"
        style={{
          background: `linear-gradient(135deg, var(--marathon-blue) 0%, var(--marathon-blue-dark) 100%)`,
          color: 'var(--white)'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div 
            className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--marathon-yellow)' }}
          >
            <MapPin size={40} style={{ color: 'var(--gray-900)' }} />
          </div>
          <h1 className="text-5xl font-extrabold mb-6">Visit Us</h1>
          <p className="text-xl" style={{ color: 'var(--gray-100)' }}>
            Stop by for quality fuel and fresh chicken
          </p>
        </div>
      </section>

      {/* Contact Info & Map */}
      <section className="py-16" style={{ backgroundColor: 'var(--gray-50)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--marathon-blue)' }}>
                Contact Information
              </h2>

              {/* Address */}
              <Card className="p-6 mb-6 border-0 shadow-lg" style={{ backgroundColor: 'var(--white)' }}>
                <div className="flex items-start space-x-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--marathon-blue-light)', color: 'var(--white)' }}
                  >
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--gray-900)' }}>
                      Address
                    </h3>
                    <p className="mb-3" style={{ color: 'var(--gray-700)' }}>
                      704 Main St<br />
                      Hope, Indiana
                    </p>
                    <a 
                      href={contactInfo.googleMapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        size="sm"
                        style={{ 
                          backgroundColor: 'var(--marathon-blue)',
                          color: 'white'
                        }}
                      >
                        Get Directions
                      </Button>
                    </a>
                  </div>
                </div>
              </Card>

              {/* Phone */}
              <Card className="p-6 mb-6 border-0 shadow-lg" style={{ backgroundColor: 'var(--white)' }}>
                <div className="flex items-start space-x-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--marathon-blue-light)', color: 'var(--white)' }}
                  >
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--gray-900)' }}>
                      Phone
                    </h3>
                    <a 
                      href="tel:8125465780"
                      className="text-lg hover:opacity-80"
                      style={{ color: 'var(--marathon-blue)' }}
                    >
                      (812) 546-5780
                    </a>
                  </div>
                </div>
              </Card>

              {/* Hours */}
              <Card className="p-6 border-0 shadow-lg" style={{ backgroundColor: 'var(--white)' }}>
                <div className="flex items-start space-x-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--marathon-blue-light)', color: 'var(--white)' }}
                  >
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-3" style={{ color: 'var(--gray-900)' }}>
                      Store Hours
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <p className="font-medium" style={{ color: 'var(--gray-800)' }}>
                          Monday - Friday
                        </p>
                        <p style={{ color: 'var(--gray-600)' }}>
                          5:00 AM - 11:00 PM
                        </p>
                      </div>
                      <div>
                        <p className="font-medium" style={{ color: 'var(--gray-800)' }}>
                          Saturday - Sunday
                        </p>
                        <p style={{ color: 'var(--gray-600)' }}>
                          6:00 AM - 11:00 PM
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--marathon-blue)' }}>
                Send Us a Message
              </h2>
              <Card className="p-8 border-0 shadow-lg" style={{ backgroundColor: 'var(--white)' }}>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-base font-medium mb-2 flex items-center space-x-2">
                      <User size={18} style={{ color: 'var(--marathon-blue)' }} />
                      <span>Your Name</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-base font-medium mb-2 flex items-center space-x-2">
                      <Mail size={18} style={{ color: 'var(--marathon-blue)' }} />
                      <span>Email Address</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-base font-medium mb-2 flex items-center space-x-2">
                      <Phone size={18} style={{ color: 'var(--marathon-blue)' }} />
                      <span>Phone Number (Optional)</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(812) 555-0123"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-medium mb-2 flex items-center space-x-2">
                      <MessageSquare size={18} style={{ color: 'var(--marathon-blue)' }} />
                      <span>Message</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      rows={5}
                      className="mt-2"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-lg py-6 font-semibold"
                    style={{ 
                      backgroundColor: 'var(--marathon-blue)',
                      color: 'white'
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16" style={{ backgroundColor: 'var(--white)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-8" style={{ color: 'var(--marathon-blue)' }}>
            Find Us on the Map
          </h2>
          <div className="rounded-lg overflow-hidden shadow-xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3056.1234567890!2d-85.7727!3d39.3037!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDE4JzEzLjMiTiA4NcKwNDYnMjEuNyJX!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Marathon Gas Station Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;
